import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form ,Offcanvas} from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import { set } from 'lodash';
import { CSVLink } from 'react-csv';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/permissions`;

const PermissionDetails = () => {
    const [permissions, setPermissions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState(null);
    const didFetchRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const WithCreatePermission = withPermission(({ children }) => children, ['permission-create']);
    const WithEditPermission = withPermission(({ children }) => children, ['permission-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['permission-delete']);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchPermissions(1, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };

    const fetchPermissions = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                },
            });

            // Directly set the grouped permissions data from the response
            setPermissions(response.data.data); // No need for the reduce method

            setPageCount(response.data.last_page);
            setCurrentPage(response.data.current_page);

        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };


    const handleAddPermission = () => {
        setFormData({ name: '' });
        setIsEdit(false);
        setSelectedPermission(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditPermission = (user) => {
        setFormData(user);
        setIsEdit(true);
        setSelectedPermission(user);
        setShowModal(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,

                        },
                    });
                    setPermissions(permissions.filter(user => user.id !== id));
                    Swal.fire(
                        "Deleted!",
                        "The permission has been deleted.",
                        "success"
                    );
                } catch (error) {
                    console.error('Error deleting permission:', error);
                    Swal.fire(
                        "Error!",
                        "Failed to delete permission. Please try again.",
                        "error"
                    );
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: undefined,
        }));
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchPermissions(newPage);
    };
    useEffect(() => {
        if (!showModal) {
            setErrors({});
        }
    }, [showModal]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';

        const duplicateInstance = permissions.some(
            (permission) => permission.name === formData.name && permission.id !== (selectedPermission?.id || null)
        );
        if (duplicateInstance) {
            newErrors.name = 'permission must be unique';
        }
        return newErrors;
    };
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        const token = userInfo?.token;
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return; // Stop here if validation fails
        }
        try {
            if (isEdit) {
                const response = await axios.put(`${API_URL}/${selectedPermission.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setPermissions(permissions.map(ind => (ind.id === selectedPermission.id ? response.data.data : ind)));
                 Swal.fire({
                                    toast: true,
                                    position: 'top-end',
                                    icon: 'success',
                                    title: 'Permission Updated Successfully',
                                    timer: 3000,
                                    timerProgressBar: false,
                                    showConfirmButton: false,
                                    didOpen: () => {
                                        const container = Swal.getContainer();
                                        container.style.top = '50px';
                                    },
                                });
            } else {
                const response = await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setPermissions([...permissions, response.data.data]);
                 Swal.fire({
                                    toast: true,
                                    position: 'top-end',
                                    icon: 'success',
                                    title: 'Permission Created Successfully',
                                    timer: 3000,
                                    timerProgressBar: false,
                                    showConfirmButton: false,
                                    didOpen: () => {
                                        const container = Swal.getContainer();
                                        container.style.top = '50px';
                                    },
                                });
            }
            setShowModal(false);
        } catch (error) {
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {

                console.error(`Unexpected error:`, error);
            }
        }
    };
    const fileInputRef = useRef(null);
    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file); // Make sure 'file' matches backend's expected name

        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            await axios.post(`${backendUrl}/api/permissions/import`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    // Include token if needed
                },
            });

            Swal.fire('Success', 'Permissions imported successfully!', 'success');
        } catch (error) {
            console.error(error.response); // Debug this to see full backend response
            Swal.fire('Error', 'Failed to import permissions', 'error');
        } finally {
            // Reset file input so same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };


    const handleExport = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Fetch all permissions (without pagination)
            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
            });

            const allPermissions = response.data.data; // Get all permissions

            if (!allPermissions || allPermissions.length === 0) {
                Swal.fire("No Data", "No permissions available to export.", "info");
                return;
            }

            const formattedData = allPermissions.map(({ id, name, created_at }) => ({
                ID: id,
                Name: name,
                CreatedAt: new Date(created_at).toLocaleDateString(),
            }));

            // Convert data to CSV
            const csv = Papa.unparse(formattedData);
            const blob = new Blob([csv], { type: "text/csv" });
            const csvURL = URL.createObjectURL(blob);

            // Create download link
            const link = document.createElement("a");
            link.href = csvURL;
            link.setAttribute("download", "permissions.csv");
            link.click();

        } catch (error) {
            console.error("Export error:", error);
            Swal.fire("Error!", "Failed to export permissions.", "error");
        }
    };


    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Permission</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><a href="javascript:void(0);">Permission</a></li>
                                        <li className="breadcrumb-item active">Permission List</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>



                                        </div>
                                        {/* <div className="d-flex align-items-center gap-2">
                                          


                                            <WithCreatePermission>
                                                <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                    onClick={handleAddPermission}
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="Add Permission"
                                                >
                                                    <i className="mdi mdi-plus"></i>
                                                </button>
                                            </WithCreatePermission>
                                            <div className="dropdown">
                                                <button
                                                    className="btn border-0"
                                                    type="button"
                                                    id="dropdownMenuButton"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                    title="More Actions"
                                                >
                                                    <i className="mdi mdi-dots-vertical"></i>
                                                </button>
                                                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                    <li>
                                                        <label htmlFor="import-file" className="dropdown-item" style={{ cursor: 'pointer' }}>
                                                            <i className="mdi mdi-upload me-2"></i> Import
                                                        </label>
                                                    </li>

                                                    <li>
                                                        <button className="dropdown-item" onClick={handleExport}>
                                                            <i className="mdi mdi-download me-2"></i> Export
                                                        </button>
                                                    </li>
                                                </ul>

                                                <input
                                                    type="file"
                                                    accept=".csv,.xlsx"
                                                    onChange={handleImport}
                                                    style={{ display: 'none' }}
                                                    id="import-file"
                                                    ref={fileInputRef}
                                                />
                                            </div>
                                        </div> */}
                                        <div className="d-flex align-items-center gap-2">
                                            {/* Add/Create Button (Rounded) */}
                                            <WithCreatePermission>
                                                <button
                                                    type="button"
                                                    className="btn rounded-circle d-flex align-items-center justify-content-center round-create-button"
                                                    onClick={handleAddPermission}
                                                    title="Add"
                                                    // style={{ width: '40px', height: '40px' }}
                                                >
                                                    <i className="mdi mdi-plus"></i>
                                                </button>
                                            </WithCreatePermission>

                                            {/* Three Dots Dropdown (No border, aligned) */}
                                            <div className="dropdown">
                                                <button
                                                    className="btn d-flex align-items-center justify-content-center"
                                                    type="button"
                                                    id="dropdownMenuButton"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                    title="More Actions"
                                                    style={{
                                                        height: '40px',
                                                        padding: '0 10px',
                                                        background: 'transparent',
                                                        boxShadow: 'none'
                                                    }}
                                                >
                                                    <i className="mdi mdi-dots-vertical font-size-22"></i>
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuButton">
                                                    <li>
                                                        <label htmlFor="import-file" className="dropdown-item" style={{ cursor: 'pointer' }}>
                                                            <i className="mdi mdi-upload me-2"></i> Import
                                                        </label>
                                                    </li>
                                                    <li>
                                                        <button className="dropdown-item" onClick={handleExport}>
                                                            <i className="mdi mdi-download me-2"></i> Export
                                                        </button>
                                                    </li>
                                                </ul>
                                                <input
                                                    type="file"
                                                    accept=".csv,.xlsx"
                                                    onChange={handleImport}
                                                    style={{ display: 'none' }}
                                                    id="import-file"
                                                    ref={fileInputRef}
                                                />
                                            </div>
                                        </div>


                                    </div>
                                    <div>
                                        <table className="table align-middle table-nowrap table-check table-list">

                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th style={{ width: '20px' }} className="align-middle">
                                                        <div className="form-check font-size-16">
                                                            <input className="form-check-input" type="checkbox" id="checkAll" />
                                                            <label className="form-check-label" htmlFor="checkAll"></label>
                                                        </div>
                                                    </th>
                                                    <th className="align-middle">#</th>
                                                    <th className="align-middle"> Name</th>
                                                    <th className="align-middle">Created On</th>

                                                    <th className="align-middle">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.isArray(permissions) && permissions.length > 0 ? (
                                                    permissions.map((user, index) => (
                                                        <tr key={user.id}>
                                                            <td>
                                                                <div className="form-check font-size-16">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`instanceCheck${index}`}
                                                                    />
                                                                    <label
                                                                        className="form-check-label"
                                                                        htmlFor={`instanceCheck${index}`}
                                                                    ></label>
                                                                </div>
                                                            </td>
                                                            <td>{index + 1}</td>
                                                            <td>{user.name}</td>
                                                            <td>{new Date(user.created_at).toLocaleDateString()}</td>

                                                            <td>
                                                                <div className="dropdown">
                                                                    <i
                                                                        className="mdi mdi-dots-horizontal font-size-18"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                        style={{ cursor: 'pointer' }}
                                                                    ></i>
                                                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                        <li><WithEditPermission>
                                                                            <button className="dropdown-item" onClick={() => handleEditPermission(user)}>
                                                                                Edit
                                                                            </button></WithEditPermission>
                                                                        </li>
                                                                        <li><WithDeletePermission>
                                                                            <button className="dropdown-item" onClick={() => handleDelete(user.id)}>
                                                                                Delete
                                                                            </button></WithDeletePermission>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No permissions found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                        />
                                    </div>
                                    {/* Modal for Add/Edit Permission */}
                                    <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement="end">
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title>{isEdit ? 'Edit Permission' : 'Add Permission'}</Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} /><br />
                                        <Offcanvas.Body>
                                            <Form>
                                                <Form.Group controlId="userName">
                                                    <Form.Label>Name</Form.Label>
                                                    <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange}
                                                        className={`form-control ${errors?.name ? 'is-invalid' : ''}`}

                                                    />
                                                    {errors.name && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.name}</div>}

                                                </Form.Group>
                                            </Form>
                                        </Offcanvas.Body>

                                        <div className="p-3 d-flex justify-content-end border-top">
                                            <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                                            <Button variant="secondary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'} </Button>
                                        </div>
                                    </Offcanvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PermissionDetails;
